import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { openWhatsApp } from "@/lib/whatsapp";

const WhatsIcon = ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M19.05 4.91A9.83 9.83 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.74.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.02Z" />
    </svg>
);

const CITY_OPTIONS = ["Dubai", "Abu Dhabi", "Sharjah", "Other UAE"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ConnectForm — the WhatsApp lead-capture form used by both ConnectModal (popup)
 * and the /contact-us page. `variant='modal'` renders compact; `variant='page'`
 * renders a larger version with header slots.
 */
export default function ConnectForm({
    variant = "modal",
    prefill = {},
    onSubmitted,
    showHeader = true,
    testIdPrefix = "connect",
}) {
    const [form, setForm] = useState({
        full_name: "",
        phone: "",
        email: "",
        city: prefill.city || "Dubai",
    });
    const [submitting, setSubmitting] = useState(false);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.full_name.trim()) return toast.error("Please enter your name");
        if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 8)
            return toast.error("Please enter a valid phone number");
        if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
            return toast.error("Please enter a valid email");
        if (!form.city) return toast.error("Please select your city");

        // Fire the lead-capture in the background — do NOT await it.
        // Awaiting here would break the user-gesture context on mobile
        // browsers, which then block the wa.me deep-link.
        setSubmitting(true);
        axios
            .post(`${API}/leads`, {
                full_name: form.full_name,
                phone: form.phone,
                bhk: null,
                city: form.city,
                source: prefill.source || (variant === "page" ? "contact_page" : "connect_modal"),
                extra: {
                    email: form.email,
                    category: prefill.category || null,
                    size: prefill.size || null,
                    services: prefill.services || null,
                    range: prefill.range || null,
                    vibe: prefill.vibe || null,
                },
            })
            .catch((err) => {
                console.warn("Lead capture failed (continued to WhatsApp):", err?.message);
            })
            .finally(() => setSubmitting(false));

        // Open WhatsApp SYNCHRONOUSLY so mobile browsers preserve the
        // user-gesture context and correctly hand off to the WhatsApp app.
        const categoryLine = prefill.category ? ` I'm interested in ${prefill.category}.` : "";
        const rangeLine = prefill.range ? ` (estimate range: ${prefill.range})` : "";
        const msg = `Hi! I'm ${form.full_name} (${form.email}), based in ${form.city}.${categoryLine}${rangeLine} I'd like to discuss my project with 45DaySpaces.`;
        openWhatsApp(msg);
        toast.success("Opening WhatsApp...");
        if (onSubmitted) onSubmitted(form);
    };

    return (
        <div className={variant === "page" ? "w-full" : ""}>
            {showHeader && (
                <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#25D366] text-white">
                        <WhatsIcon className="h-6 w-6" />
                    </span>
                    <div>
                        <div className="font-display text-xl font-extrabold text-left">
                            Connect With Our Specialist
                        </div>
                        <div className="text-sm text-black/60 text-left mt-1">
                            Fill in your details and we&apos;ll connect you instantly on WhatsApp.
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className={`${showHeader ? "mt-6" : ""} space-y-4`} data-testid={`${testIdPrefix}-form`}>
                <div>
                    <Label htmlFor={`${testIdPrefix}-full_name`} className="text-xs font-bold tracking-wider">FULL NAME</Label>
                    <Input
                        id={`${testIdPrefix}-full_name`}
                        placeholder="Your full name"
                        value={form.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        data-testid={`${testIdPrefix}-name-input`}
                        className="mt-1.5 h-11 rounded-xl"
                    />
                </div>

                <div>
                    <Label htmlFor={`${testIdPrefix}-phone`} className="text-xs font-bold tracking-wider">PHONE NUMBER</Label>
                    <Input
                        id={`${testIdPrefix}-phone`}
                        type="tel"
                        placeholder="+971 XX XXX XXXX"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        data-testid={`${testIdPrefix}-phone-input`}
                        className="mt-1.5 h-11 rounded-xl"
                    />
                </div>

                <div>
                    <Label htmlFor={`${testIdPrefix}-email`} className="text-xs font-bold tracking-wider">EMAIL</Label>
                    <Input
                        id={`${testIdPrefix}-email`}
                        type="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        data-testid={`${testIdPrefix}-email-input`}
                        className="mt-1.5 h-11 rounded-xl"
                    />
                </div>

                <div>
                    <Label className="text-xs font-bold tracking-wider">YOUR CITY</Label>
                    <Select value={form.city} onValueChange={(v) => update("city", v)}>
                        <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid={`${testIdPrefix}-city-select`}>
                            <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                            {CITY_OPTIONS.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid={`${testIdPrefix}-submit-btn`}
                    className="btn-pill btn-lime w-full justify-center pr-1.5 mt-2"
                >
                    <span className="px-1 text-sm font-bold">{submitting ? "CONNECTING..." : "CONNECT ON WHATSAPP"}</span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-[#CCFF00]">
                        <ArrowRight className="h-4 w-4" />
                    </span>
                </button>

                <p className="text-[11px] text-black/55 text-center inline-flex items-center justify-center w-full gap-1.5">
                    <Lock className="h-3 w-3" />
                    Your details are private. We never spam.
                </p>
            </form>
        </div>
    );
}
